import * as React from 'react';
import { FormResult } from '../types';

import { useMutation } from '@apollo/client';
import * as UpdateFormResult from 'core/gql/mutations/update-form-result';
import { useRegistrationParticipants } from 'hooks/registrationParticipants';
import { useState } from 'react';
import DeleteFormResultModal from '../DeleteFormResultModal';
import ExportModal from '../ExportModal';
import PostRegEditDrawer from '../PostRegEditDrawer/PostRegEditDrawer';
import PostRegEditLogDrawer from '../PostRegEditLogDrawer/PostRegEditLogDrawer';
import RosteringModal from '../RosteringModal';
import SendEmailModal, { EmailType } from '../SendEmailModal';
import WaitlistModal from '../WaitlistModal/WaitlistModal';
import { formatFilters } from './helpers';
import {
  exportDataToCSV,
  exportSelectedRowsToCSV,
  handleFormResultDeletion,
  sendEmailRequest,
} from './modalActions';
import { ModalType, ParticipantListModal } from './types';

// TODO: Create custom hook to handle participant list logic
interface Props {
  modalProps: ParticipantListModal;
  onClose: () => void;
  selectedFormResult?: FormResult;
  orgId: string;
  formId: string;
  setSelectedFormResult: (x: FormResult) => void;
  selectedRecords: any[];
  setSelectedRecords: (x: any[]) => void;
  emailType: EmailType;
  resetEmailStates: () => void;
  totalRegistrants: number;
  exportModalTitle: string;
  sortOrder: 'asc' | 'desc';
  sortBy: string;
  refetchFormResults: () => void;
  exportModalColumns: any[];
}

export function ParticipantListModals({
  modalProps,
  onClose,
  selectedFormResult,
  orgId,
  formId,
  exportModalTitle,
  setSelectedFormResult,
  resetEmailStates,
  selectedRecords,
  emailType,
  setSelectedRecords,
  totalRegistrants,
  refetchFormResults,
  sortOrder,
  sortBy,
  exportModalColumns,
}: Props) {
  const [selectedRole, setSelectedRole] = useState<string>('player');
  const { searchTerm, activeFilters } = useRegistrationParticipants();
  const [updateFormResult] = useMutation(UpdateFormResult.MUTATION);

  const exportParams = React.useMemo(
    () => ({
      searchTerm,
      sortOrder,
      sortBy,
      filters: formatFilters(activeFilters),
      columns: [],
    }),
    [sortBy, sortOrder, searchTerm, activeFilters]
  );

  const sendRosteringRequest = () => {
    onClose();
    setSelectedRole('player');
    setSelectedFormResult(null);
    refetchFormResults();
  };

  const { isOpen, modalType } = modalProps;
  const showModal = (modal: ModalType) => isOpen && modal === modalType;

  return (
    <>
      <PostRegEditDrawer
        refetch={refetchFormResults}
        formResult={selectedFormResult}
        isPostRegEditDrawerOpen={showModal(ModalType.POST_REG_EDIT)}
        setIsPostRegEditDrawerOpen={(isOpen) =>
          !isOpen ? onClose() : () => {}
        }
      />

      <PostRegEditLogDrawer
        orgId={+orgId}
        formResult={selectedFormResult}
        isOpen={showModal(ModalType.POST_REG_EDIT_LOGS)}
        setIsOpen={(isOpen) => (!isOpen ? onClose() : () => {})}
      />

      <ExportModal
        organizationId={+orgId}
        onSubmit={(columns, exportType) => {
          exportModalTitle === 'Generate Export'
            ? exportDataToCSV(
                columns,
                exportType,
                orgId,
                formId,
                exportParams,
                onClose
              )
            : exportSelectedRowsToCSV(columns, selectedRecords, onClose);
        }}
        show={showModal(ModalType.EXPORT)}
        title={exportModalTitle}
        onClose={onClose}
        columns={exportModalColumns}
      />
      <RosteringModal
        orgId={orgId}
        formId={formId}
        onSubmit={sendRosteringRequest}
        show={showModal(ModalType.ROSTER)}
        selectedRecords={selectedRecords}
        selectedFormResult={selectedFormResult}
        onClose={() => {
          onClose();
          setSelectedRole('player');
          setSelectedFormResult(null);
        }}
        selectedRole={selectedRole}
        setSelectedRole={(role) => setSelectedRole(role)}
      />

      <SendEmailModal
        totalRegistrants={totalRegistrants}
        selectedRecordsLength={selectedRecords?.length || 0}
        show={showModal(ModalType.EMAIL)}
        isSendingEmailToAllRegistrants={emailType === 'all'}
        isSendingIndividualEmail={emailType === 'individual'}
        selectedIndividualEmail={selectedFormResult?.registrantEmail}
        onClose={onClose}
        onSubmit={(args) =>
          sendEmailRequest(
            args,
            emailType === 'all',
            emailType === 'individual',
            selectedRecords,
            selectedFormResult,
            orgId,
            formId,
            searchTerm,
            formatFilters,
            activeFilters,
            resetEmailStates
          )
        }
        fromEmail={null}
      />

      <WaitlistModal
        orgId={orgId}
        formId={formId}
        type={modalType === ModalType.OFFER ? 'accept' : 'release'}
        selectedRegistrants={
          selectedFormResult ? [selectedFormResult] : selectedRecords
        }
        show={
          showModal(ModalType.OFFER) || showModal(ModalType.REMOVE_WAITLIST)
        }
        onClose={() => {
          setSelectedFormResult(null);
          onClose();
        }}
        onSubmit={() => {
          // A timeout is needed to allow Org-Reporting-Service to update the data and build the form.
          setTimeout(() => {
            refetchFormResults();
          }, 1500);

          setSelectedRecords([]);
          setSelectedFormResult(null);
          onClose();
        }}
      />

      {modalType === ModalType.DELETE_FORM_RESULT && (
        <DeleteFormResultModal
          isOpen={showModal(ModalType.DELETE_FORM_RESULT)}
          onClose={() => {
            setSelectedFormResult(null);
            onClose();
          }}
          onSubmit={() =>
            handleFormResultDeletion(
              selectedFormResult,
              selectedRecords,
              updateFormResult,
              setSelectedRecords,
              setSelectedFormResult,
              onClose,
              refetchFormResults
            )
          }
          selectedFormResults={
            selectedFormResult ? [selectedFormResult] : selectedRecords
          }
        />
      )}
    </>
  );
}
